
import React, { useState, useEffect } from 'react';
import { Flag, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContentReports } from '@/hooks/useContentReports';
import { ReportContentModal } from './ReportContentModal';

interface ReportButtonProps {
  contentId: string;
  contentType: 'post' | 'cast' | 'comment';
  contentPreview?: string;
}

export const ReportButton: React.FC<ReportButtonProps> = ({
  contentId,
  contentType,
  contentPreview = ''
}) => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [hasReported, setHasReported] = useState(false);
  const { checkIfReported } = useContentReports();

  const checkReportStatus = async () => {
    try {
      // Only check if contentId is a valid UUID format
      if (!contentId || typeof contentId !== 'string' || contentId.length < 10) {
        return;
      }
      
      const reported = await checkIfReported(contentId, contentType);
      setHasReported(reported);
    } catch (error) {
      console.error('Error checking report status:', error);
      // Don't set hasReported if there's an error - just leave it as false
    }
  };

  useEffect(() => {
    checkReportStatus();
  }, [contentId, contentType]);

  const handleReportSuccess = () => {
    setHasReported(true);
    setShowReportModal(false);
  };

  // Don't render button for invalid content IDs
  if (!contentId || typeof contentId !== 'string' || contentId.length < 10) {
    return null;
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowReportModal(true)}
        disabled={hasReported}
        className="h-8 w-8 p-0 text-gray-500 hover:text-red-500 disabled:opacity-50"
        title={hasReported ? "Already reported" : "Report content"}
      >
        {hasReported ? (
          <AlertCircle size={16} className="text-red-500" />
        ) : (
          <Flag size={16} />
        )}
      </Button>

      {showReportModal && (
        <ReportContentModal
          isOpen={showReportModal}
          contentId={contentId}
          contentType={contentType}
          contentPreview={contentPreview}
          onClose={() => setShowReportModal(false)}
        />
      )}
    </>
  );
};
